import React, { useState, useCallback, useEffect, useLayoutEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import { flushSync } from 'react-dom'
import { 
  Plus, 
  Trash2, 
  Edit, 
  Save, 
  X, 
  Database,
  Globe,
  GitBranch,
  List,
  CheckCircle
} from 'lucide-react'
import { FlowConfigData, FlowStep, FlowStepConfig } from '../types/flowConfig.types'
import { getDefaultFlowStepConfig } from '../utils/flowStepDefaults'
import { updateFlowStepInConfig } from '../utils/flowStepUpdate'
import { pruneUiLayoutToSteps } from '../utils/flowUiLayout'
import { logger } from '../utils/logger'
import {
  ApiDataEditor,
  ConditionalConditionsEditor,
  FlatKeyValueObjectEditor,
  ListOptionsTableEditor,
  type StepOption
} from './flow-editors'

export interface FlowEditorHandle {
  /** Dispara a mesma validação e persistência do botão Salvar. */
  save: () => Promise<void>
  getConfig: () => FlowConfigData
  /** Substitui o rascunho completo (ex.: edição estrutural no diagrama visual). */
  replaceConfig: (next: FlowConfigData) => void
}

export interface FlowEditorProps {
  flowConfig: FlowConfigData
  onSave: (config: FlowConfigData) => void
  onCancel: () => void
  isEditing?: boolean
  /** Notifica o pai sempre que o rascunho do fluxo muda (ex.: sincronizar o diagrama visual). */
  onConfigChange?: (config: FlowConfigData) => void
}

const FlowEditor = forwardRef<FlowEditorHandle, FlowEditorProps>(function FlowEditor(
  { flowConfig, onSave, onCancel, isEditing = false, onConfigChange },
  ref
) {
  logger.debug('🔍 DEBUG FlowEditor: Props recebidas:', { flowConfig, isEditing })
  
  const [config, setConfig] = useState<FlowConfigData>(flowConfig)

  /** Evita reexecutar o layout effect só porque o pai passou nova referência de `onConfigChange` (re-renders do FlowConfigManager etc.). */
  const onConfigChangeRef = useRef(onConfigChange)
  onConfigChangeRef.current = onConfigChange

  // Debug: Log quando config muda
  logger.debug('🔍 DEBUG FlowEditor: Config atual:', config)
  const [selectedStep, setSelectedStep] = useState<string | null>(null)
  const [isAddingStep, setIsAddingStep] = useState(false)
  const [newStepType, setNewStepType] = useState<FlowStep['type']>('data_collection')
  const [isSaving, setIsSaving] = useState(false)
  
  // Debug: Log quando o componente é renderizado
  logger.debug('🔍 DEBUG FlowEditor: Componente renderizado, isSaving:', isSaving)
  
  // Monitorar mudanças no config
  useEffect(() => {
    logger.debug('🔍 DEBUG FlowEditor useEffect: Config mudou:', config)
  }, [config])

  /** Só quando `config` muda — dependência em `onConfigChange` causava sync espúrio e sobrescrevia o diagrama com estado antigo do editor. */
  useLayoutEffect(() => {
    onConfigChangeRef.current?.(config)
  }, [config])

  const stepTypes = [
    { type: 'data_collection', label: 'Coleta de Dados', icon: Database, color: 'bg-blue-500' },
    { type: 'credential_ref', label: 'Credencial Externa', icon: Database, color: 'bg-cyan-600' },
    { type: 'auth_session', label: 'Sessão de Autenticação', icon: Globe, color: 'bg-emerald-600' },
    { type: 'api_call', label: 'Chamada API', icon: Globe, color: 'bg-green-500' },
    { type: 'conditional', label: 'Condicional', icon: GitBranch, color: 'bg-yellow-500' },
    { type: 'dynamic_conditional', label: 'Condicional Dinâmica', icon: GitBranch, color: 'bg-orange-500' },
    { type: 'list_selection', label: 'Seleção de Lista', icon: List, color: 'bg-purple-500' },
    { type: 'dynamic_list', label: 'Lista Dinâmica', icon: List, color: 'bg-violet-600' },
    { type: 'item_selection', label: 'Seleção de Item (API)', icon: List, color: 'bg-violet-700' },
    { type: 'message_dispatch', label: 'Despacho de Mensagem', icon: CheckCircle, color: 'bg-indigo-600' },
    { type: 'transfer_human', label: 'Transferir para Atendente', icon: CheckCircle, color: 'bg-teal-500' },
    { type: 'finalization', label: 'Finalização', icon: CheckCircle, color: 'bg-red-500' }
  ]

  const handleAddStep = useCallback(() => {
    logger.debug('🔍 DEBUG FlowEditor handleAddStep: Adicionando nova etapa...')
    const stepId = `step_${Date.now()}`
    const newStep: FlowStep = {
      id: stepId,
      type: newStepType,
      name: `Nova Etapa ${config.steps ? Object.keys(config.steps).length + 1 : 1}`,
      description: '',
      config: getDefaultConfig(newStepType)
    }

    logger.debug('🔍 DEBUG FlowEditor handleAddStep: Nova etapa criada:', newStep)

    setConfig(prev => {
      const newConfig = {
        ...prev,
        steps: {
          ...prev.steps,
          [stepId]: newStep
        }
      }
      logger.debug('🔍 DEBUG FlowEditor handleAddStep: Config atualizado:', newConfig)
      return newConfig
    })

    setSelectedStep(stepId)
    setIsAddingStep(false)
    logger.debug('🔍 DEBUG FlowEditor handleAddStep: Etapa adicionada com sucesso')
  }, [newStepType, config.steps])

  const handleDeleteStep = useCallback((stepId: string) => {
    flushSync(() => {
      setConfig((prev) => {
        const newSteps = { ...prev.steps }
        delete newSteps[stepId]

        const withoutStep: FlowConfigData = {
          ...prev,
          steps: newSteps,
          start_step: prev.start_step === stepId ? Object.keys(newSteps)[0] || '' : prev.start_step
        }
        return pruneUiLayoutToSteps(withoutStep)
      })
    })

    if (selectedStep === stepId) {
      setSelectedStep(null)
    }
  }, [selectedStep])

  const handleUpdateStep = useCallback((stepId: string, updates: Partial<FlowStep>) => {
    logger.debug('🔍 DEBUG FlowEditor handleUpdateStep: Atualizando step:', stepId, updates)
    setConfig((prev) => {
      const result = updateFlowStepInConfig(prev, stepId, updates)
      if (!result) {
        logger.warn('🔍 DEBUG FlowEditor handleUpdateStep: Step não encontrado ou atualização ignorada:', stepId)
        return prev
      }
      if (result.renamedTo) {
        queueMicrotask(() => setSelectedStep(result.renamedTo!))
      }
      logger.debug('🔍 DEBUG FlowEditor handleUpdateStep: Config atualizado:', result.config)
      return result.config
    })
  }, [])

  const handleSave = useCallback(async () => {
    logger.debug('🔍 DEBUG FlowEditor handleSave: Iniciando salvamento...')
    logger.debug('🔍 DEBUG FlowEditor handleSave: Config atual:', config)

    if (!config.name || !config.name.trim()) {
      logger.error('🔍 DEBUG FlowEditor handleSave: Nome do fluxo é obrigatório')
      alert('Nome do fluxo é obrigatório')
      return
    }

    if (!config.steps || !config.start_step) {
      logger.error('🔍 DEBUG FlowEditor handleSave: Config inválido - faltam steps ou start_step')
      alert('Configuração inválida: faltam steps ou start_step')
      return
    }

    logger.debug('🔍 DEBUG FlowEditor handleSave: Config válido, prosseguindo...')
    setIsSaving(true)
    try {
      await onSave(config)
      logger.debug('🔍 DEBUG FlowEditor handleSave: Salvamento concluído com sucesso')
    } catch (error) {
      logger.error('🔍 DEBUG FlowEditor handleSave: Erro no salvamento:', error)
    } finally {
      setIsSaving(false)
    }
  }, [config, onSave])

  useImperativeHandle(
    ref,
    () => ({
      save: () => handleSave(),
      getConfig: () => config,
      /** Síncrono: evita o próximo `getConfig()` no pai ainda ver passos já removidos no diagrama. */
      replaceConfig: (next: FlowConfigData) => {
        flushSync(() => {
          setConfig(next)
        })
      }
    }),
    [handleSave, config]
  )

  const getDefaultConfig = (type: FlowStep['type']): FlowStepConfig => getDefaultFlowStepConfig(type)

  const getStepIcon = (type: FlowStep['type']) => {
    const stepType = stepTypes.find(st => st.type === type)
    return stepType ? stepType.icon : Database
  }

  const getStepColor = (type: FlowStep['type']) => {
    const stepType = stepTypes.find(st => st.type === type)
    return stepType ? stepType.color : 'bg-gray-500'
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4">
        {/* Título e Botões */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditing ? 'Editar Fluxo' : 'Criar Novo Fluxo'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Configure as etapas do seu fluxo conversacional
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={onCancel}
              className="flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </button>
            <button
              onClick={() => {
                logger.debug('🔍 DEBUG FlowEditor: Botão Salvar clicado!')
                handleSave()
              }}
              disabled={isSaving}
              className="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
        
        {/* Campos de Nome e Descrição */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="flow-name" className="block text-sm font-medium text-gray-700">
              Nome do Fluxo *
            </label>
            <input
              type="text"
              id="flow-name"
              value={config.name || ''}
              onChange={(e) => setConfig(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Ex: Consulta de Boletos"
              required
            />
          </div>
          <div>
            <label htmlFor="flow-description" className="block text-sm font-medium text-gray-700">
              Descrição
            </label>
            <input
              type="text"
              id="flow-description"
              value={config.description || ''}
              onChange={(e) => setConfig(prev => ({ ...prev, description: e.target.value }))}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Ex: Fluxo para consultar boletos em aberto"
            />
          </div>
        </div>
      </div>

      <div className="flex-1 flex">
        {/* Sidebar - Lista de Etapas */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Etapas do Fluxo</h3>
            <p className="text-sm text-gray-600 mt-1">
              {config.steps ? Object.keys(config.steps).length : 0} etapa(s) configurada(s)
            </p>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-2">
              {config.steps ? Object.entries(config.steps).map(([stepId, step]) => {
                const Icon = getStepIcon(step.type)
                const colorClass = getStepColor(step.type)
                
                return (
                  <div
                    key={stepId}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedStep === stepId
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedStep(stepId)}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${colorClass}`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {step.name}
                        </p>
                        <p className="text-xs text-gray-500 capitalize">
                          {step.type.replace('_', ' ')}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteStep(stepId)
                        }}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )
              }) : (
                <div className="text-center py-4">
                  <p className="text-gray-500 text-sm">Nenhuma etapa encontrada</p>
                  <p className="text-gray-400 text-xs">Adicione uma etapa para começar</p>
                </div>
              )}
            </div>

            {/* Botão para adicionar etapa */}
            <div className="mt-4">
              {isAddingStep ? (
                <div className="space-y-2">
                  <select
                    value={newStepType}
                    onChange={(e) => setNewStepType(e.target.value as FlowStep['type'])}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {stepTypes.map(({ type, label }) => (
                      <option key={type} value={type}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleAddStep}
                      className="flex-1 px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Adicionar
                    </button>
                    <button
                      onClick={() => setIsAddingStep(false)}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setIsAddingStep(true)}
                  className="w-full px-3 py-2 text-sm font-medium text-gray-700 bg-white border-2 border-dashed border-gray-300 rounded-md hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Etapa
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content - Editor de Etapa */}
        <div className="flex-1 flex flex-col">
          {selectedStep ? (
            <div className="flex-1 overflow-y-auto p-6">
              <StepEditor
                step={config.steps?.[selectedStep]}
                onUpdate={(updates) => handleUpdateStep(selectedStep, updates)}
                availableSteps={Object.entries(config.steps || {}).map(([id, s]) => ({
                  id,
                  name: s?.name || id
                }))}
              />
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <Edit className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Selecione uma etapa para editar
                </h3>
                <p className="text-gray-600">
                  Clique em uma etapa na barra lateral para começar a editar
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
})

/** Props do formulário por tipo de passo (reutilizado no modo Visual). */
export interface StepEditorProps {
  step: FlowStep
  onUpdate: (updates: Partial<FlowStep>) => void
  /** Etapas do fluxo (para selects em condicional, etc.). */
  availableSteps?: StepOption[]
}

export const StepEditor: React.FC<StepEditorProps> = ({ step, onUpdate, availableSteps = [] }) => {
  logger.debug('🔍 DEBUG StepEditor: Renderizando step:', step)
  logger.debug('🔍 DEBUG StepEditor: onUpdate function:', typeof onUpdate)
  
  // Verificar se step existe
  if (!step) {
    logger.debug('🔍 DEBUG StepEditor: Step não existe, retornando mensagem')
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Nenhuma etapa selecionada</p>
      </div>
    )
  }

  const handleConfigUpdate = (configUpdates: Partial<FlowStepConfig>) => {
    logger.debug('🔍 DEBUG StepEditor handleConfigUpdate: Atualizando config:', configUpdates)
    logger.debug('🔍 DEBUG StepEditor handleConfigUpdate: Step atual:', step)
    onUpdate({
      config: {
        ...(step.config || {}),
        ...configUpdates
      }
    })
    logger.debug('🔍 DEBUG StepEditor handleConfigUpdate: onUpdate chamado')
  }

  const renderStepEditor = () => {
    switch (step.type) {
      case 'data_collection':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prompt para o usuário
              </label>
              <textarea
                value={step.config?.prompt || ''}
                onChange={(e) => handleConfigUpdate({ prompt: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Digite o prompt que será exibido para o usuário..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de validação
              </label>
              <select
                value={step.config?.validation || 'text'}
                onChange={(e) => handleConfigUpdate({ validation: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="text">Texto</option>
                <option value="cpf">CPF</option>
                <option value="cnpj">CNPJ</option>
                <option value="email">Email</option>
                <option value="phone">Telefone</option>
                <option value="number">Número</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome do campo para armazenar (opcional)
              </label>
              <input
                type="text"
                value={step.config?.storage_field || step.config?.field_name || ''}
                onChange={(e) => handleConfigUpdate({ storage_field: e.target.value, field_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="collected_value"
              />
              <p className="text-xs text-gray-500 mt-1">
                Sem API: deixe URL da API em branco. O valor será validado e armazenado localmente.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL da API (opcional – se vazio, só valida e armazena)
              </label>
              <input
                type="url"
                value={step.config?.api_url || ''}
                onChange={(e) => handleConfigUpdate({ api_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mensagem de sucesso (opcional, pode usar variáveis)
              </label>
              <input
                type="text"
                value={step.config?.success_template || ''}
                onChange={(e) => handleConfigUpdate({ success_template: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Informação registrada."
              />
            </div>
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="dc_suppress_success"
                checked={step.config?.suppress_success_message === true}
                onChange={(e) => handleConfigUpdate({ suppress_success_message: e.target.checked })}
                className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <div>
                <label htmlFor="dc_suppress_success" className="block text-sm text-gray-700">
                  Não enviar mensagem padrão ao avançar (sem texto de sucesso acima)
                </label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Evita &quot;Informação registrada com sucesso.&quot; quando o próximo passo (ex.: API) já responde ao usuário.
                </p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mensagem de erro de validação (opcional)
              </label>
              <input
                type="text"
                value={step.config?.error_message || ''}
                onChange={(e) => handleConfigUpdate({ error_message: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Dados inválidos. Tente novamente."
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="required"
                checked={step.config?.required || false}
                onChange={(e) => handleConfigUpdate({ required: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="required" className="ml-2 block text-sm text-gray-700">
                Campo obrigatório
              </label>
            </div>
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="skip_template_attempts"
                checked={step.config?.skip_template_attempts === true}
                onChange={(e) => handleConfigUpdate({ skip_template_attempts: e.target.checked })}
                className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <div>
                <label htmlFor="skip_template_attempts" className="block text-sm text-gray-700">
                  Ignorar controle de tentativas do template (CPF/CNPJ)
                </label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Quando desmarcado, aplica o limite configurado em Templates → o mesmo template ligado ao menu deste fluxo (habilitar &quot;Controle de tentativas&quot; lá).
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="finalize_on_attempts_limit"
                checked={step.config?.finalize_on_attempts_limit !== false}
                onChange={(e) => handleConfigUpdate({ finalize_on_attempts_limit: e.target.checked })}
                className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <div>
                <label htmlFor="finalize_on_attempts_limit" className="block text-sm text-gray-700">
                  Ao atingir o limite de tentativas, finalizar a conversa
                </label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Recomendado: evita deixar o status em &quot;aguardando entrada&quot; sem próximo passo. Desmarque só se quiser que o usuário continue no fluxo após a mensagem de limite.
                </p>
              </div>
            </div>
          </div>
        )

      case 'api_call':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL da API
              </label>
              <input
                type="url"
                value={step.config?.api_url || ''}
                onChange={(e) => handleConfigUpdate({ api_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://api.exemplo.com/endpoint"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Método HTTP
              </label>
              <select
                value={step.config?.api_method || 'GET'}
                onChange={(e) => handleConfigUpdate({ api_method: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fonte de autenticação
              </label>
              <select
                value={step.config?.auth_source || 'manual'}
                onChange={(e) => handleConfigUpdate({ auth_source: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="manual">Token manual (Bearer)</option>
                <option value="credential">Credencial externa</option>
                <option value="auth_session">Token da etapa auth_session</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chave da credencial (quando auth_source=credential)
              </label>
              <input
                type="text"
                value={step.config?.credential_key || ''}
                onChange={(e) => handleConfigUpdate({ credential_key: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="mk_auth_provedor"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Campo do token no contexto (quando auth_source=auth_session)
              </label>
              <input
                type="text"
                value={step.config?.auth_token_field || ''}
                onChange={(e) => handleConfigUpdate({ auth_token_field: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="auth.jwt"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Token de Autenticação
              </label>
              <input
                type="text"
                value={step.config?.api_token || ''}
                onChange={(e) => handleConfigUpdate({ api_token: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Digite o token de autenticação"
              />
              <p className="text-xs text-gray-500 mt-1">
                Token será enviado no header Authorization: Bearer {step.config?.api_token || 'TOKEN'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Template de Sucesso
              </label>
              <textarea
                value={step.config?.success_template || ''}
                onChange={(e) => handleConfigUpdate({ success_template: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Template exibido quando a API retorna sucesso..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dados da API (corpo enviado)
              </label>
              <ApiDataEditor
                apiData={
                  step.config?.api_data && typeof step.config.api_data === 'object' && !Array.isArray(step.config.api_data)
                    ? (step.config.api_data as Record<string, unknown>)
                    : {}
                }
                onChange={(next) => handleConfigUpdate({ api_data: next })}
              />
              <p className="text-xs text-gray-500 mt-1">
                Dados enviados à API. Use variáveis como {`{{cpf}}`}, {`{{user_input}}`}, etc. Objetos aninhados: modo JSON
                avançado.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mapeamento da resposta (response_mapping)
              </label>
              <FlatKeyValueObjectEditor
                value={
                  Object.fromEntries(
                    Object.entries(step.config?.response_mapping || {}).map(([k, v]) => [k, String(v)])
                  ) as Record<string, string>
                }
                onChange={(next) => handleConfigUpdate({ response_mapping: next })}
              />
              <p className="text-xs text-gray-500 mt-1">
                Chave = campo no contexto do fluxo; valor = caminho na resposta JSON do webhook (notação com pontos).
              </p>
            </div>
            <div className="rounded-md border border-amber-200 bg-amber-50/40 p-3 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Controle de tentativas: quando registrar
                </label>
                <select
                  value={String(step.config?.attempts_record_mode || 'on_data_collection')}
                  onChange={(e) =>
                    handleConfigUpdate({
                      attempts_record_mode: e.target.value as 'on_data_collection' | 'on_api_success'
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="on_data_collection">No data_collection (comportamento padrão)</option>
                  <option value="on_api_success">Somente no sucesso deste API call</option>
                </select>
                <p className="text-xs text-gray-600 mt-1">
                  Use <code className="bg-white px-1 rounded">on_api_success</code> quando a tentativa só deve contar após
                  operação concluída (ex.: desbloqueio efetivo).
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Campo do documento no contexto
                  </label>
                  <input
                    type="text"
                    value={String(step.config?.attempts_document_field || '')}
                    onChange={(e) => handleConfigUpdate({ attempts_document_field: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="ex.: cpf"
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    Obrigatório para <code className="bg-white px-1 rounded">on_api_success</code>.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo do documento
                  </label>
                  <select
                    value={String(step.config?.attempts_validation_type || 'document')}
                    onChange={(e) =>
                      handleConfigUpdate({
                        attempts_validation_type: e.target.value as 'cpf' | 'cnpj' | 'document'
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="document">Documento (auto)</option>
                    <option value="cpf">CPF</option>
                    <option value="cnpj">CNPJ</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="http_empty_on_404"
                checked={
                  step.config?.http_empty_response_on_404 === true ||
                  step.config?.mk_auth_titulos_404_as_empty === true
                }
                onChange={(e) =>
                  handleConfigUpdate({
                    http_empty_response_on_404: e.target.checked,
                    mk_auth_titulos_404_as_empty: false
                  })
                }
                className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <div>
                <label htmlFor="http_empty_on_404" className="block text-sm text-gray-700">
                  GET: HTTP 404 = resposta vazia (sem erro)
                </label>
                <p className="text-xs text-gray-500 mt-0.5">
                  APIs que usam 404 para &quot;nenhum registo&quot; em listagens. Corpo sintético: titulos [], Total 0.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="api_require_success_true"
                checked={step.config?.require_success_true === true}
                onChange={(e) => handleConfigUpdate({ require_success_true: e.target.checked })}
                className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <div>
                <label htmlFor="api_require_success_true" className="block text-sm text-gray-700">
                  Exigir <code className="bg-white px-1 rounded">success: true</code> no retorno da API
                </label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Recomendado para desbloqueio: evita marcar sucesso quando o endpoint retorna HTTP 200 com erro de negócio.
                </p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Template de Erro
              </label>
              <textarea
                value={step.config?.error_template || ''}
                onChange={(e) => handleConfigUpdate({ error_template: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="Template exibido quando a API retorna erro..."
              />
            </div>
          </div>
        )

      case 'credential_ref':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chave da credencial
              </label>
              <input
                type="text"
                value={step.config?.credential_key || ''}
                onChange={(e) => handleConfigUpdate({ credential_key: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="mk_auth_provedor"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use a chave cadastrada em Flow Configs / Credentials.
              </p>
            </div>
          </div>
        )

      case 'auth_session':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chave da credencial
              </label>
              <input
                type="text"
                value={step.config?.credential_key || ''}
                onChange={(e) => handleConfigUpdate({ credential_key: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="mk_auth_provedor"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Endpoint de autenticação
              </label>
              <input
                type="text"
                value={step.config?.auth_endpoint || ''}
                onChange={(e) => handleConfigUpdate({ auth_endpoint: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="/api/cliente/show/test"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Caminho do token na resposta
              </label>
              <input
                type="text"
                value={step.config?.token_response_path || '$raw'}
                onChange={(e) => handleConfigUpdate({ token_response_path: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="$raw"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Salvar token como
              </label>
              <input
                type="text"
                value={step.config?.store_as || 'auth.jwt'}
                onChange={(e) => handleConfigUpdate({ store_as: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="auth.jwt"
              />
            </div>
          </div>
        )

      case 'conditional':
        return (
          <ConditionalConditionsEditor
            config={step.config || {}}
            onConfigUpdate={handleConfigUpdate}
            availableSteps={availableSteps}
          />
        )

      case 'dynamic_conditional':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Condições Dinâmicas
              </label>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Condição base (finalizar)
                  </label>
                  <input
                    type="text"
                    value={step.config?.dynamic_conditions?.base_condition || ''}
                    onChange={(e) => handleConfigUpdate({ 
                      dynamic_conditions: {
                        ...step.config?.dynamic_conditions,
                        base_condition: e.target.value
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder='user_input == "0"'
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template base
                  </label>
                  <textarea
                    value={step.config?.dynamic_conditions?.base_template || ''}
                    onChange={(e) => handleConfigUpdate({ 
                      dynamic_conditions: {
                        ...step.config?.dynamic_conditions,
                        base_template: e.target.value
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder="Template para finalizar"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template para itens
                  </label>
                  <textarea
                    value={step.config?.dynamic_conditions?.item_template || ''}
                    onChange={(e) => handleConfigUpdate({ 
                      dynamic_conditions: {
                        ...step.config?.dynamic_conditions,
                        item_template: e.target.value
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder="Template para seleção de item (use {item_number} e {boleto_detalhes_{item_number}})"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template padrão
                  </label>
                  <textarea
                    value={step.config?.dynamic_conditions?.default_template || ''}
                    onChange={(e) => handleConfigUpdate({ 
                      dynamic_conditions: {
                        ...step.config?.dynamic_conditions,
                        default_template: e.target.value
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    placeholder="Template para opção inválida (use {boleto_count})"
                  />
                </div>
              </div>
            </div>
          </div>
        )

      case 'list_selection':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mensagem inicial (opcional; uma quebra até a lista é adicionada no envio)
              </label>
              <textarea
                value={step.config?.prompt_message || ''}
                onChange={(e) => handleConfigUpdate({ prompt_message: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="Ex: Escolha uma cidade:"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Campo de exibição (chave no JSON de cada opção)
              </label>
              <input
                type="text"
                value={step.config?.display_field || ''}
                onChange={(e) => handleConfigUpdate({ display_field: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Campo de valor (chave no JSON de cada opção)
              </label>
              <input
                type="text"
                value={step.config?.value_field || ''}
                onChange={(e) => handleConfigUpdate({ value_field: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="id"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lista de opções
              </label>
              <ListOptionsTableEditor
                listData={step.config?.list_data}
                valueField={step.config?.value_field || 'id'}
                displayField={step.config?.display_field || 'name'}
                onChange={(next) => handleConfigUpdate({ list_data: next })}
              />
              <p className="text-xs text-gray-500 mt-1">
                Uma linha por opção; texto mostrado ao utilizador e valor armazenado usam as chaves acima (predefinição:
                name e id).
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Campo para armazenar seleção
              </label>
              <input
                type="text"
                value={step.config?.storage_field || ''}
                onChange={(e) => handleConfigUpdate({ storage_field: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="selected_value"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Instrução de seleção
              </label>
              <input
                type="text"
                value={step.config?.selection_instruction || ''}
                onChange={(e) => handleConfigUpdate({ selection_instruction: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Digite o número da opção desejada."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mensagem de opção inválida
              </label>
              <input
                type="text"
                value={step.config?.invalid_option_message || ''}
                onChange={(e) => handleConfigUpdate({ invalid_option_message: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Opção inválida. Digite um número da lista."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mensagem após escolha (pode usar {`{{selected_display}}`})
              </label>
              <input
                type="text"
                value={step.config?.success_template || ''}
                onChange={(e) => handleConfigUpdate({ success_template: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Você escolheu: {{selected_display}}"
              />
            </div>
          </div>
        )

      case 'dynamic_list':
      case 'item_selection':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Campo de origem da lista no contexto
              </label>
              <input
                type="text"
                value={step.config?.items_source_field || 'last_api_response_items'}
                onChange={(e) => handleConfigUpdate({ items_source_field: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="last_api_response_items"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Caminho da lista no contexto (opcional, prioridade)
              </label>
              <input
                type="text"
                value={step.config?.items_path || ''}
                onChange={(e) => handleConfigUpdate({ items_path: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="ex.: last_api_response.api_data.titulos"
              />
              <p className="text-xs text-gray-500 mt-1">
                Se preenchido, substitui a leitura apenas por items_source_field.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Campo de exibição</label>
              <input
                type="text"
                value={step.config?.display_field || 'name'}
                onChange={(e) => handleConfigUpdate({ display_field: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Campo de valor</label>
              <input
                type="text"
                value={step.config?.value_field || 'id'}
                onChange={(e) => handleConfigUpdate({ value_field: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="id"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Template de linha (opcional, ex.: {'{{descricao}} — {{vencimento}}'})
              </label>
              <input
                type="text"
                value={step.config?.display_template || ''}
                onChange={(e) => handleConfigUpdate({ display_template: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder=""
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Armazenar seleção em</label>
              <input
                type="text"
                value={step.config?.storage_field || 'selected_value'}
                onChange={(e) => handleConfigUpdate({ storage_field: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="selected_value"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Índice numérico (1-based) em
              </label>
              <input
                type="text"
                value={step.config?.store_selection_index_as || 'selected_item'}
                onChange={(e) => handleConfigUpdate({ store_selection_index_as: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="selected_item"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Objeto da linha em (opcional)
              </label>
              <input
                type="text"
                value={step.config?.store_selected_object_as || ''}
                onChange={(e) => handleConfigUpdate({ store_selected_object_as: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="selected_row"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mensagem inicial (cabeçalho antes da lista; uma quebra até as linhas é adicionada automaticamente)
              </label>
              <textarea
                value={step.config?.prompt_message || ''}
                onChange={(e) => handleConfigUpdate({ prompt_message: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder={'📊 Encontrados {{titulos_count}} boletos:'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mensagem de opção inválida</label>
              <input
                type="text"
                value={step.config?.invalid_option_message || ''}
                onChange={(e) => handleConfigUpdate({ invalid_option_message: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Opção inválida. Digite um número da lista."
              />
            </div>
          </div>
        )

      case 'message_dispatch':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Modo</label>
              <select
                value={step.config?.mode || 'single'}
                onChange={(e) => handleConfigUpdate({ mode: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="single">Mensagem única</option>
                <option value="multiple">Múltiplas mensagens</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Campo de origem dos dados</label>
              <input
                type="text"
                value={step.config?.source_field || 'last_api_response'}
                onChange={(e) => handleConfigUpdate({ source_field: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="last_api_response"
              />
              <p className="text-xs text-gray-500 mt-1">Caminho no contexto com pontos (ex.: last_api_response.resultado).</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Caminho do array de mensagens (modo múltiplas)
              </label>
              <input
                type="text"
                value={step.config?.messages_path || ''}
                onChange={(e) => handleConfigUpdate({ messages_path: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="messages ou body.linhas"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Caminho do texto (único ou fallback)
              </label>
              <input
                type="text"
                value={step.config?.single_message_path || ''}
                onChange={(e) => handleConfigUpdate({ single_message_path: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="formatted_message (padrão legado se vazio)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Caminho should_finalize (relativo à origem)
              </label>
              <input
                type="text"
                value={step.config?.should_finalize_path || ''}
                onChange={(e) => handleConfigUpdate({ should_finalize_path: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="should_finalize"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mensagem fallback</label>
              <input
                type="text"
                value={step.config?.fallback_message || ''}
                onChange={(e) => handleConfigUpdate({ fallback_message: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Processamento concluído."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Delay entre mensagens (ms)
              </label>
              <input
                type="number"
                min={0}
                value={step.config?.delay_between_messages_ms ?? 1000}
                onChange={(e) => handleConfigUpdate({ delay_between_messages_ms: Number(e.target.value || 0) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )

      case 'transfer_human':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título na mensagem de transferência (opcional)
              </label>
              <input
                type="text"
                value={step.config?.transfer_title || ''}
                onChange={(e) => handleConfigUpdate({ transfer_title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: Solicitação de Instalação"
              />
              <p className="text-xs text-gray-500 mt-1">
                Se vazio, será usado o título do menu.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mensagem ao transferir (opcional)
              </label>
              <textarea
                value={step.config?.transfer_message || ''}
                onChange={(e) => handleConfigUpdate({ transfer_message: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Ex: Recebemos seus dados e estamos encaminhando para um atendente..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Se vazio, será usada a mensagem padrão do sistema (Mensagens do bot).
              </p>
            </div>
          </div>
        )

      case 'finalization':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mensagem de finalização
              </label>
              <textarea
                value={step.config?.message || ''}
                onChange={(e) => handleConfigUpdate({ message: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Digite a mensagem que será exibida ao finalizar o fluxo..."
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="clear_data"
                checked={step.config?.clear_data || false}
                onChange={(e) => handleConfigUpdate({ clear_data: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="clear_data" className="ml-2 block text-sm text-gray-700">
                Limpar dados temporários
              </label>
            </div>
          </div>
        )

      default:
        return (
          <div className="text-center py-8">
            <p className="text-gray-500">Tipo de etapa não suportado</p>
          </div>
        )
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${getStepColor(step.type)}`}>
              {React.createElement(getStepIcon(step.type), { className: "w-5 h-5 text-white" })}
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">{step.name}</h3>
              <p className="text-sm text-gray-600 capitalize">
                {step.type.replace('_', ' ')}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-6">
            {/* ID da etapa (usado em start_step, next_step, etc.) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID da etapa *
              </label>
              <input
                type="text"
                value={step.id}
                onChange={(e) => onUpdate({ id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: step_cidades, step_planos..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Use um identificador único, que será referenciado em &quot;Próxima etapa&quot; e nas condições (ex.: step_cidades).
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome da etapa
              </label>
              <input
                type="text"
                value={step.name}
                onChange={(e) => onUpdate({ name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Digite o nome da etapa..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrição
              </label>
              <textarea
                value={step.description || ''}
                onChange={(e) => onUpdate({ description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="Descreva o que esta etapa faz..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Próxima etapa
              </label>
              <input
                type="text"
                value={step.next_step || ''}
                onChange={(e) => onUpdate({ next_step: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="ID da próxima etapa (opcional)"
              />
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Configurações específicas</h4>
              {renderStepEditor()}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Funções auxiliares (duplicadas no componente para uso no StepEditor - usar stepTypes quando possível)
const getStepIcon = (type: FlowStep['type']) => {
  const icons: Record<string, React.ComponentType<{ className?: string }>> = {
    data_collection: Database,
    credential_ref: Database,
    auth_session: Globe,
    api_call: Globe,
    conditional: GitBranch,
    dynamic_conditional: GitBranch,
    list_selection: List,
    dynamic_list: List,
    item_selection: List,
    message_dispatch: CheckCircle,
    transfer_human: CheckCircle,
    finalization: CheckCircle
  }
  return icons[type] || Database
}

const getStepColor = (type: FlowStep['type']) => {
  const colors: Record<string, string> = {
    data_collection: 'bg-blue-500',
    credential_ref: 'bg-cyan-600',
    auth_session: 'bg-emerald-600',
    api_call: 'bg-green-500',
    conditional: 'bg-yellow-500',
    dynamic_conditional: 'bg-orange-500',
    list_selection: 'bg-purple-500',
    dynamic_list: 'bg-violet-600',
    item_selection: 'bg-violet-700',
    message_dispatch: 'bg-indigo-600',
    transfer_human: 'bg-teal-500',
    finalization: 'bg-red-500'
  }
  return colors[type] || 'bg-gray-500'
}

export default FlowEditor

